// Client-side DOCX export for Side-by-Side Competitive Report
// Mirrors utils/exportDocx.js style and helpers where appropriate
import {
  AlignmentType,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  Document,
  ImageRun,
  BorderStyle,
  WidthType,
  TableLayoutType,
  Header,
  Footer,
  PageBreak,
  Bookmark,
  InternalHyperlink,
  VerticalAlign
} from 'docx';
import askpotLogo from '../assets/images/askpot_logo_transparent.png';

// Helpers (adapted from exportDocx.js)
const createHeading = (text, level = HeadingLevel.HEADING_2, bookmarkId = null) => [
  new Paragraph({ text: '', spacing: { after: 400 } }),
  new Paragraph({
    children: bookmarkId
      ? [
          new Bookmark({
            id: bookmarkId,
            children: [
              new TextRun({
                text: text || '',
                bold: true,
                color: level === HeadingLevel.HEADING_1 ? '2E7D32' : '388E3C',
                size: level === HeadingLevel.HEADING_1 ? 32 : 28
              })
            ]
          })
        ]
      : [
          new TextRun({
            text: text || '',
            bold: true,
            color: level === HeadingLevel.HEADING_1 ? '2E7D32' : '388E3C',
            size: level === HeadingLevel.HEADING_1 ? 32 : 28
          })
        ]
  }),
  new Paragraph({ text: '', spacing: { after: 200 } })
];

// Larger heading specifically for company names in the Companies Overview
const createCompanyHeading = (text) => [
  new Paragraph({ text: '', spacing: { after: 400 } }),
  new Paragraph({
    children: [
      new TextRun({
        text: text || '',
        bold: true,
        color: '2E7D32',
        size: 40
      })
    ]
  }),
  new Paragraph({ text: '', spacing: { after: 200 } })
];

const createSectionDivider = () =>
  new Paragraph({
    children: [
      new TextRun({
        text: '_______________________________________________',
        color: '2E7D32',
        size: 24
      })
    ],
    alignment: AlignmentType.CENTER,
    spacing: { before: 600, after: 600 }
  });

const createText = (text) => new Paragraph({ text: text ?? '', spacing: { before: 200, after: 200 } });

const createBulletList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const uniqueItems = [
    ...new Set(items.filter((item) => item && (item.text || item.description || (typeof item === 'string' && item.trim() !== ''))))
  ];
  return uniqueItems.map((item) => {
    const str = typeof item === 'string' ? item : item.text || item.description || JSON.stringify(item);
    return new Paragraph({ text: str, bullet: { level: 0 }, spacing: { before: 120, after: 120 } });
  });
};

// KPI helpers for Overview tiles in SBS report
const createKpiCell = (label, value, subtitle, colorHex) =>
  new TableCell({
    children: [
      new Paragraph({
        children: [new TextRun({ text: label || '', bold: true, color: colorHex || '3F51B5' })],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [new TextRun({ text: String(value ?? 'N/A'), bold: true })],
        spacing: { before: 100, after: 100 }
      }),
      new Paragraph({
        children: [new TextRun({ text: subtitle || '', color: '666666' })]
      })
    ],
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4 },
      bottom: { style: BorderStyle.SINGLE, size: 4 },
      left: { style: BorderStyle.SINGLE, size: 4 },
      right: { style: BorderStyle.SINGLE, size: 4 }
    }
  });

const createStyledTable = (headers, rows, customWidths = null) => {
  const totalWidth = 9000;
  const columnWidths = customWidths
    ? customWidths.map((w) => Math.floor(totalWidth * w))
    : headers.map(() => Math.floor(totalWidth / headers.length));

  const tableRows = [
    new TableRow({
      children: headers.map(
        (header, index) =>
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: header, bold: true })] })],
            width: { size: columnWidths[index], type: WidthType.DXA },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 4 },
              bottom: { style: BorderStyle.SINGLE, size: 4 },
              left: { style: BorderStyle.SINGLE, size: 4 },
              right: { style: BorderStyle.SINGLE, size: 4 }
            }
          })
      )
    }),
    ...rows.map(
      (row) =>
        new TableRow({
          children: row.map(
            (cell, index) =>
              new TableCell({
                children: [new Paragraph({ text: String(cell ?? 'N/A') })],
                width: { size: columnWidths[index], type: WidthType.DXA },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 4 },
                  bottom: { style: BorderStyle.SINGLE, size: 4 },
                  left: { style: BorderStyle.SINGLE, size: 4 },
                  right: { style: BorderStyle.SINGLE, size: 4 }
                }
              })
          )
        })
    )
  ];

  return new Table({
    rows: tableRows,
    width: { size: totalWidth, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: columnWidths,
    margins: { top: 200, bottom: 200, left: 0, right: 0 }
  });
};

const dataURLToUint8Array = async (dataURL) => {
  const res = await fetch(dataURL);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
};

// Draw a 2x2 Trust vs User Base matrix and return data URL
const createTrustUserBaseMatrix = (quad) => {
  const canvas = document.createElement('canvas');
  canvas.width = 960;
  canvas.height = 560;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const margin = { top: 40, right: 40, bottom: 60, left: 60 };
  const w = canvas.width - margin.left - margin.right;
  const h = canvas.height - margin.top - margin.bottom;
  const cx = margin.left;
  const cy = margin.top;

  // Grid rectangles
  const halfW = Math.floor(w / 2);
  const halfH = Math.floor(h / 2);
  const quads = [
    { x: cx, y: cy, title: 'High Trust, Small User Base', items: quad?.high_trust_small_base },
    { x: cx + halfW, y: cy, title: 'High Trust, Large User Base', items: quad?.high_trust_large_base },
    { x: cx, y: cy + halfH, title: 'Low Trust, Small User Base', items: quad?.low_trust_small_base },
    { x: cx + halfW, y: cy + halfH, title: 'Low Trust, Large User Base', items: quad?.low_trust_large_base }
  ];

  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1.5;
  quads.forEach((q) => {
    ctx.fillStyle = '#f8faf5';
    ctx.fillRect(q.x, q.y, halfW - 10, halfH - 10);
    ctx.strokeRect(q.x, q.y, halfW - 10, halfH - 10);
  });

  // Titles
  ctx.fillStyle = '#2E7D32';
  ctx.font = 'bold 16px Arial';
  quads.forEach((q) => {
    ctx.fillText(q.title, q.x + 10, q.y + 22);
  });

  // Companies as small cards in each quadrant
  const toList = (arr) => {
    if (!arr) return [];
    if (Array.isArray(arr)) return arr.map((v) => (typeof v === 'string' ? v : v?.company || v?.name || String(v)));
    return [];
  };

  ctx.font = '12px Arial';
  quads.forEach((q) => {
    const items = toList(q.items);
    let x = q.x + 12;
    let y = q.y + 42;
    const maxCardW = halfW - 40;
    const cardH = 28;
    const vGap = 10;
    items.forEach((label) => {
      // wrap to next column if overflow
      if (y + cardH > q.y + halfH - 16) {
        x += Math.min(180, maxCardW / 2);
        y = q.y + 42;
      }
      const text = String(label);
      const padding = 8;
      const textW = Math.min(ctx.measureText(text).width, maxCardW - padding * 2);
      const cardW = Math.min(Math.ceil(textW + padding * 2), 220);
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#cfd8dc';
      ctx.lineWidth = 1;
      ctx.fillRect(x, y, cardW, cardH);
      ctx.strokeRect(x, y, cardW, cardH);
      ctx.fillStyle = '#333333';
      ctx.textAlign = 'left';
      ctx.fillText(text, x + padding, y + 18);
      y += cardH + vGap;
    });
  });

  // Axes labels
  ctx.fillStyle = '#666666';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('User Base (Small → Large)', cx + w / 2, canvas.height - 22);
  ctx.save();
  ctx.translate(22, cy + h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText('Trust (Low → High)', 0, 0);
  ctx.restore();

  return canvas.toDataURL('image/png');
};

// Draw a generic 2x2 matrix for parameter combinations (x vs y) and return data URL
const createParamPairMatrix = (xLabel, yLabel, quads) => {
  const canvas = document.createElement('canvas');
  canvas.width = 960;
  canvas.height = 560;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const margin = { top: 40, right: 40, bottom: 60, left: 60 };
  const w = canvas.width - margin.left - margin.right;
  const h = canvas.height - margin.top - margin.bottom;
  const cx = margin.left;
  const cy = margin.top;
  const halfW = Math.floor(w / 2);
  const halfH = Math.floor(h / 2);

  const boxes = [
    { x: cx, y: cy, title: `High ${yLabel}, Low ${xLabel}`, items: quads.top_left },
    { x: cx + halfW, y: cy, title: `High ${yLabel}, High ${xLabel}`, items: quads.top_right },
    { x: cx, y: cy + halfH, title: `Low ${yLabel}, Low ${xLabel}`, items: quads.bottom_left },
    { x: cx + halfW, y: cy + halfH, title: `Low ${yLabel}, High ${xLabel}`, items: quads.bottom_right }
  ];

  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 1.5;
  boxes.forEach((b, i) => {
    ctx.fillStyle = i % 2 === 0 ? '#edf7ed' : '#f8fbf8';
    ctx.fillRect(b.x, b.y, halfW - 10, halfH - 10);
    ctx.strokeRect(b.x, b.y, halfW - 10, halfH - 10);
  });

  ctx.fillStyle = '#2E7D32';
  ctx.font = 'bold 16px Arial';
  boxes.forEach((b) => ctx.fillText(b.title, b.x + 10, b.y + 22));

  const toList = (arr) => (Array.isArray(arr) ? arr : []);
  ctx.font = '12px Arial';
  boxes.forEach((b) => {
    const items = toList(b.items);
    let x = b.x + 12;
    let y = b.y + 42;
    const maxCardW = halfW - 40;
    const cardH = 28;
    const vGap = 10;
    items.forEach((label) => {
      if (y + cardH > b.y + halfH - 16) {
        x += Math.min(180, maxCardW / 2);
        y = b.y + 42;
      }
      const text = String(label);
      const padding = 8;
      const textW = Math.min(ctx.measureText(text).width, maxCardW - padding * 2);
      const cardW = Math.min(Math.ceil(textW + padding * 2), 220);
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#cfd8dc';
      ctx.lineWidth = 1;
      ctx.fillRect(x, y, cardW, cardH);
      ctx.strokeRect(x, y, cardW, cardH);
      ctx.fillStyle = '#333333';
      ctx.textAlign = 'left';
      ctx.fillText(text, x + padding, y + 18);
      y += cardH + vGap;
    });
  });

  // Axes labels
  ctx.fillStyle = '#666666';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(`${xLabel} (Low → High)`, cx + w / 2, canvas.height - 22);
  ctx.save();
  ctx.translate(22, cy + h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`${yLabel} (Low → High)`, 0, 0);
  ctx.restore();

  return canvas.toDataURL('image/png');
};

const loadAskpotLogo = async () => {
  try {
    const response = await fetch(askpotLogo);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Utility formatters
const safeJoin = (arr, sep = '\n• ') => (Array.isArray(arr) && arr.length ? `• ${arr.join(sep)}` : 'N/A');
const getServiceName = (overviewResult) => {
  if (!Array.isArray(overviewResult)) return null;
  const item = overviewResult.find((obj) => obj && Object.prototype.hasOwnProperty.call(obj, 'Service Name'));
  return item ? item['Service Name'] : null;
};
const getFieldFromOverview = (overviewResult, key) => {
  if (!Array.isArray(overviewResult)) return null;
  const item = overviewResult.find((obj) => obj && Object.prototype.hasOwnProperty.call(obj, key));
  return item ? item[key] : null;
};

export async function exportSbsDocx(analysisResults, fileName) {
  const children = [];

  // Logo
  const logoDataUrl = await loadAskpotLogo();
  let logoBytes = null;
  if (logoDataUrl) logoBytes = await dataURLToUint8Array(logoDataUrl);

  // Title page
  const title = 'Side-by-Side Competitive Report';
  const reportMeta = analysisResults?.side_by_side_analysis?.analysis?.report_metadata;
  const titlePageContent = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 1200 },
      children: logoBytes ? [new ImageRun({ data: logoBytes, transformation: { width: 200, height: 55 } })] : []
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      children: [new TextRun({ text: title, size: 48, color: '6B8E23' })]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [new TextRun({ text: reportMeta?.market ? `Market: ${reportMeta.market}` : '', size: 20 })]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      children: [new TextRun({ text: 'Analysis Report', size: 36, bold: true })]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: `Generated on: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
          size: 18
        })
      ]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: 'Powered by Askpot AI', size: 16, color: '666666', italic: true })]
    })
  ];

  // TOC
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'Table of Contents', size: 32, bold: true, color: '2E7D32' })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 800, after: 600 }
    })
  );
  const tocEntries = [
    { text: '1. Overview', anchor: 'overview' },
    { text: '2. Strategic Analysis', anchor: 'strategic-analysis' },
    { text: '3. USP & Distinctions', anchor: 'usp-dist' },
    { text: '4. Market Trends & Benchmarks', anchor: 'mtb' },
    { text: '5. Field-by-Field Analysis', anchor: 'fbf' },
    { text: '6. Side-by-Side Analysis', anchor: 'sbs' },
    { text: '7. Pricing Analysis', anchor: 'pricing-analysis' },
    { text: 'Appendix 1: Data Normalization & Gaps', anchor: 'appendix-data-normalization' },
    { text: 'Appendix 2: Companies Overview', anchor: 'companies-overview' }
  ];
  tocEntries.forEach((entry, index) => {
    const [number, ...titleParts] = entry.text.split('. ');
    const titleText = titleParts.join('. ');
    const isLast = index === tocEntries.length - 1;
    children.push(
      new Paragraph({
        children: [
          new InternalHyperlink({
            children: [new TextRun({ text: `${number}. ${titleText}`, size: 18, style: 'Hyperlink' })],
            anchor: entry.anchor
          }),
          ...(isLast ? [new PageBreak()] : [])
        ],
        spacing: { after: 200 },
        indent: { left: 400 }
      })
    );
  });

  // 1. Overview (metadata, KPIs, Suggested Actions, Competitive White Spaces)
  children.push(createSectionDivider());
  children.push(...createHeading('1. Overview', HeadingLevel.HEADING_1, 'overview'));
  {
    const sbs = analysisResults?.side_by_side_analysis?.analysis || {};
    // Metadata
    if (sbs?.report_metadata) {
      children.push(...createHeading('Report Metadata', HeadingLevel.HEADING_2));
      const meta = sbs.report_metadata;
      if (meta.title) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Title: ', bold: true }), new TextRun({ text: String(meta.title) })],
            spacing: { before: 200, after: 200 }
          })
        );
      }
      if (meta.market) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Market: ', bold: true }), new TextRun({ text: String(meta.market) })],
            spacing: { before: 200, after: 200 }
          })
        );
      }

      if (Array.isArray(meta.companies_analyzed)) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Companies: ', bold: true }), new TextRun({ text: meta.companies_analyzed.join(', ') })],
            spacing: { before: 200, after: 200 }
          })
        );
      }
      // Add space before KPI table to separate from metadata
      children.push(new Paragraph({ text: '', spacing: { after: 400 } }));
    }

    // KPIs
    const reportMeta = sbs?.report_metadata || {};
    const companiesAnalyzed = Array.isArray(reportMeta.companies_analyzed) ? reportMeta.companies_analyzed : [];
    const scoreRows = sbs?.['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];
    const weights = (sbs?.['4_weight_assignment'] || []).reduce((acc, w) => {
      acc[w.id] = Number(w.weight_percent) || 0;
      return acc;
    }, {});
    const params = scoreRows.length ? Object.keys(scoreRows[0]).filter((k) => k !== 'company' && k !== 'total') : Object.keys(weights);
    const competitiveIndex = sbs?.['6_competitive_index'] || [];
    const top = competitiveIndex.reduce((best, cur) => (Number(cur.score || 0) > Number(best.score || 0) ? cur : best), {
      company: '',
      score: 0
    });
    const whiteSpaces = sbs?.['9_strategic_analysis']?.competitive_white_spaces || [];

    const kpiTable = new Table({
      width: { size: 9000, type: WidthType.DXA },
      layout: TableLayoutType.FIXED,
      columnWidths: [2250, 2250, 2250, 2250],
      rows: [
        new TableRow({
          children: [
            createKpiCell('Companies Analyzed', companiesAnalyzed.length, companiesAnalyzed.join(', '), '3F51B5'),
            createKpiCell('Parameters Evaluated', params.length, 'Pricing, Reviews, Speed, Features, etc.', '0288D1'),
            createKpiCell('Top Score', top.score ?? 'N/A', `${top.company || ''} leads the market`, '2E7D32'),
            createKpiCell('Strategic Opportunities', whiteSpaces.length || 0, 'Identified market white spaces', 'F9A825')
          ]
        })
      ]
    });
    children.push(kpiTable);

    // Suggested Actions
    const execRecs = sbs?.['10_executive_recommendations'] || [];
    if (Array.isArray(execRecs) && execRecs.length) {
      children.push(...createHeading('Suggested Actions', HeadingLevel.HEADING_2));
      children.push(...createBulletList(execRecs));
    }

    // Strategic Analysis (move up) without the white spaces table (already covered)
    if (sbs?.['9_strategic_analysis']) {
      children.push(...createHeading('2. Strategic Analysis', HeadingLevel.HEADING_1, 'strategic-analysis'));
      const sa = sbs['9_strategic_analysis'];
      if (Array.isArray(sa.competitive_threats) && sa.competitive_threats.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Competitive Threats', bold: true })] }));
        children.push(...createBulletList(sa.competitive_threats));
      }
      if (sa.vulnerability_analysis) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Vulnerability Analysis', bold: true })] }));
        ['dependencies', 'strategic_risks', 'defensive_posture'].forEach((k) => {
          if (sa.vulnerability_analysis[k]) {
            const pretty = k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
            children.push(
              new Paragraph({
                children: [new TextRun({ text: `${pretty}: `, bold: true }), new TextRun({ text: String(sa.vulnerability_analysis[k]) })]
              })
            );
          }
        });
      }
    }

    // Competitive White Spaces
    if (Array.isArray(whiteSpaces) && whiteSpaces.length) {
      children.push(...createHeading('Competitive White Spaces', HeadingLevel.HEADING_2));
      whiteSpaces.forEach((ws, idx) => {
        const title = ws?.market_need || `Opportunity ${idx + 1}`;
        children.push(new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 24, color: '2E7D32' })] }));
        const chips = [`Opportunity: ${ws?.opportunity_score ?? 'N/A'}/10`, `Time to execute: ${ws?.time_to_market ?? 'N/A'}`];
        children.push(createText(chips.join(' | ')));
        if (ws?.opportunity) children.push(createText(ws.opportunity));
        if (ws?.addressed_by) children.push(createText(`Currently addressed by: ${ws.addressed_by}`));
      });
    }
  }

  // Prepare companies list for later sections and appendices
  const companies = Array.isArray(analysisResults?.analysis_results) ? analysisResults.analysis_results : [];

  // Appendices moved to bottom after Pricing Analysis (see insertion before document build)

  // 3. USP & Distinctions
  children.push(createSectionDivider());
  children.push(...createHeading('3. USP & Distinctions', HeadingLevel.HEADING_1, 'usp-dist'));
  if (companies.length > 0) {
    const rows = companies.map((c) => [
      getServiceName(c.overview_result) || c.url || 'Company',
      safeJoin(c?.usp?.USP || []),
      safeJoin(c?.usp?.DISTINCTION || [])
    ]);
    children.push(createStyledTable(['Company', 'USP', 'Distinctions'], rows, [0.2, 0.4, 0.4]));
  }

  // 4. Market Trends & Benchmarks (per-company)
  children.push(createSectionDivider());
  children.push(...createHeading('4. Market Trends & Benchmarks', HeadingLevel.HEADING_1, 'mtb'));
  if (companies.length > 0) {
    companies.forEach((company) => {
      const name = getServiceName(company.overview_result) || company.url || 'Company';
      children.push(...createCompanyHeading(name));

      const mt = company.market_trends || {};
      // Trends
      children.push(...createHeading('Market Trends'));
      if (Array.isArray(mt.trends) && mt.trends.length) {
        mt.trends.forEach((trend, i) => {
          children.push(...createHeading(`Trend ${i + 1}: ${trend?.title || 'Untitled'}`, HeadingLevel.HEADING_3));
          if (trend?.description) children.push(createText(trend.description));
          if (trend?.industry_relevance) children.push(createText(`Industry Relevance: ${trend.industry_relevance}`));
          if (trend?.source && (trend.source.page || trend.source.document)) {
            const src = `${trend.source.document || ''}${trend.source.page ? `, p. ${trend.source.page}` : ''}`.trim();
            if (src) children.push(createText(`Source: ${src}`));
          }
        });
      } else {
        children.push(createText('No trends information available.'));
      }

      // Benchmarks
      children.push(...createHeading('Industry Benchmarks'));
      const benchmarksArr = Array.isArray(mt.benchmarks)
        ? mt.benchmarks
        : mt.benchmarks && typeof mt.benchmarks === 'object'
        ? Object.values(mt.benchmarks)
        : [];
      if (benchmarksArr.length) {
        benchmarksArr.forEach((benchmark) => {
          const title = `${benchmark?.metric_value || 'N/A'} ${benchmark?.metric_name || 'Untitled'}`.trim();
          children.push(...createHeading(title, HeadingLevel.HEADING_3));
          if (benchmark?.description && benchmark.description !== 'No description available.') {
            children.push(createText(benchmark.description));
          }
          if (benchmark?.industry_relevance) {
            children.push(createText(`Industry Relevance: ${benchmark.industry_relevance}`));
          }
          if (benchmark?.source && (benchmark.source.page || benchmark.source.document)) {
            const src = `${benchmark.source.document || ''}${benchmark.source.page ? `, p. ${benchmark.source.page}` : ''}`.trim();
            if (src) children.push(createText(`Source: ${src}`));
          }
        });
      } else {
        children.push(createText('No benchmarks available.'));
      }
    });
  } else {
    children.push(createText('No companies available.'));
  }

  // 5. Field-by-Field Analysis – interleaved tables + insights
  children.push(createSectionDivider());
  children.push(...createHeading('5. Field-by-Field Analysis', HeadingLevel.HEADING_1, 'fbf'));
  const fbf = analysisResults?.field_by_field_analysis?.analysis?.field_analysis || {};
  const normalizeKey = (name) => (name || '').toLowerCase().replace(/\s+/g, '_');

  // Gather union of overview fields to create tables for
  const fieldSet = new Set();
  companies.forEach((c) => {
    (Array.isArray(c.overview_result) ? c.overview_result : []).forEach((item) => {
      const key = item && typeof item === 'object' ? Object.keys(item)[0] : null;
      if (key && key !== 'Service Name') fieldSet.add(key);
    });
  });

  const preferredOrder = [
    'Main Value Proposition',
    'USP',
    'Competitive advantages',
    'Strengths',
    'Differentiation',
    'CTA',
    'Pricing',
    'Audience',
    'Industry',
    'Use Cases',
    'Description',
    'Positioning',
    'Key Features',
    'Social Proof',
    'Tone of Voice',
    'Trust Factors',
    'Brand Positioning'
  ];

  const fieldsOrdered = Array.from(fieldSet).sort((a, b) => {
    const ai = preferredOrder.indexOf(a);
    const bi = preferredOrder.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  const coveredFbfKeys = new Set();

  fieldsOrdered.forEach((fieldName) => {
    const hasAny = companies.some((c) => !!getFieldFromOverview(c.overview_result, fieldName));
    const fbfKey = normalizeKey(fieldName);
    const section = fbf[fbfKey];
    if (section) coveredFbfKeys.add(fbfKey);
    if (!hasAny && !section) return;

    children.push(...createHeading(fieldName, HeadingLevel.HEADING_2));

    // Overview table
    if (hasAny) {
      const rows = companies.map((c) => [
        getServiceName(c.overview_result) || c.url || 'Company',
        getFieldFromOverview(c.overview_result, fieldName) || 'N/A'
      ]);
      children.push(createStyledTable(['Company', fieldName], rows, [0.25, 0.75]));
    }

    // Field Insights
    if (section) {
      if (Array.isArray(section?.insights) && section.insights.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Field Insights', bold: true })] }));
        children.push(...createBulletList(section.insights));
      }
      if (section?.my_company && typeof section.my_company === 'object') {
        children.push(new Paragraph({ children: [new TextRun({ text: 'My Company', bold: true })] }));
        Object.entries(section.my_company).forEach(([k, v]) => {
          children.push(createText(`${k.replace(/_/g, ' ')}: ${v}`));
        });
      }
      if (Array.isArray(section?.recommendations) && section.recommendations.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Recommendations', bold: true })] }));
        children.push(...createBulletList(section.recommendations));
      }
      if (Array.isArray(section?.competitors_highlights) && section.competitors_highlights.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Competitors Highlights', bold: true })] }));
        children.push(...createBulletList(section.competitors_highlights));
      }
    }
  });

  // Render any remaining FBF sections that didn't have an overview field
  Object.keys(fbf).forEach((key) => {
    if (coveredFbfKeys.has(key)) return;
    const section = fbf[key];
    children.push(
      ...createHeading(
        key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
        HeadingLevel.HEADING_2
      )
    );
    if (Array.isArray(section?.insights) && section.insights.length) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'Field Insights', bold: true })] }));
      children.push(...createBulletList(section.insights));
    }
    if (section?.my_company && typeof section.my_company === 'object') {
      children.push(new Paragraph({ children: [new TextRun({ text: 'My Company', bold: true })] }));
      Object.entries(section.my_company).forEach(([k, v]) => {
        children.push(createText(`${k.replace(/_/g, ' ')}: ${v}`));
      });
    }
    if (Array.isArray(section?.recommendations) && section.recommendations.length) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'Recommendations', bold: true })] }));
      children.push(...createBulletList(section.recommendations));
    }
    if (Array.isArray(section?.competitors_highlights) && section.competitors_highlights.length) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'Competitors Highlights', bold: true })] }));
      children.push(...createBulletList(section.competitors_highlights));
    }
  });

  // 6. Side-by-Side Analysis
  children.push(createSectionDivider());
  children.push(...createHeading('6. Side-by-Side Analysis', HeadingLevel.HEADING_1, 'sbs'));
  const sbs = analysisResults?.side_by_side_analysis?.analysis || {};

  if (Array.isArray(sbs?.['4_weight_assignment'])) {
    const rows = sbs['4_weight_assignment'].map((w) => [w.id || 'N/A', w.rationale || 'N/A', String(w.weight_percent ?? 'N/A')]);
    children.push(...createHeading('Weight Assignment', HeadingLevel.HEADING_2));
    children.push(createStyledTable(['Parameter', 'Rationale', 'Weight %'], rows, [0.25, 0.55, 0.2]));
  }

  if (Array.isArray(sbs?.['6_competitive_index'])) {
    const rows = sbs['6_competitive_index'].map((r) => [r.company || 'N/A', String(r.score ?? 'N/A')]);
    children.push(...createHeading('Competitive Index', HeadingLevel.HEADING_2));
    children.push(createStyledTable(['Company', 'Score'], rows, [0.7, 0.3]));

    // Render stacked parameter scores chart (normalized to full height) using canvas
    try {
      const params =
        (sbs['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || []).length > 0
          ? Object.keys(sbs['7_multi_dimensional_comparison_matrices'].A_heatmap_scorecard[0]).filter(
              (k) => k !== 'company' && k !== 'total'
            )
          : [];
      const scoreRows = sbs['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];
      if (params.length && scoreRows.length) {
        // Weight map for contributions (bars reflect true totals out of 100)
        const weights = (sbs['4_weight_assignment'] || []).reduce((acc, w) => {
          acc[w.id] = Number(w.weight_percent) || 0;
          return acc;
        }, {});
        const weightsTotal = Object.values(weights).reduce((a, b) => a + b, 0) || 100;
        const canvas = document.createElement('canvas');
        canvas.width = 960;
        canvas.height = 520;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const margin = { top: 60, right: 20, bottom: 90, left: 70 };
        const chartW = canvas.width - margin.left - margin.right;
        const chartH = canvas.height - margin.top - margin.bottom;
        const x0 = margin.left;
        const y0 = margin.top + chartH;

        const companiesList = scoreRows.map((r) => r.company);
        // Normalize each bar to full height by using each company's total as 100%
        const barWidth = (chartW / companiesList.length) * 0.6;
        const spacing = chartW / companiesList.length;

        const colors = ['#7E57C2', '#757575', '#43A047', '#FBC02D', '#E53935', '#26C6DA', '#9FA8DA', '#A5D6A7'];

        // horizontal grid lines and y labels (0..100)
        ctx.strokeStyle = '#e0e0e0';
        ctx.fillStyle = '#666';
        ctx.font = '12px Arial';
        ctx.textAlign = 'right';
        for (let p = 0; p <= 5; p++) {
          const value = p * 20;
          const y = margin.top + chartH - (value / 100) * chartH;
          ctx.beginPath();
          ctx.moveTo(x0, y);
          ctx.lineTo(x0 + chartW, y);
          ctx.stroke();
          ctx.fillText(String(value), x0 - 8, y + 4);
        }

        companiesList.forEach((company, idx) => {
          let yStack = 0;
          // compute weighted contributions per parameter (weight% * score/5)
          const contribs = params.map((param) => (weights[param] || 0) * ((scoreRows[idx][param] || 0) / 5));
          params.forEach((param, pIdx) => {
            const contrib = contribs[pIdx];
            const h = (contrib / weightsTotal) * chartH; // 0..100 scaling
            const x = x0 + idx * spacing + (spacing - barWidth) / 2;
            const y = y0 - yStack - h;
            ctx.fillStyle = colors[pIdx % colors.length];
            ctx.fillRect(x, y, barWidth, h);
            // value label if segment tall enough
            if (h >= 14) {
              ctx.fillStyle = '#fff';
              ctx.font = '11px Arial';
              ctx.textAlign = 'center';
              ctx.fillText(String(scoreRows[idx][param] || 0), x + barWidth / 2, y + h / 2 + 4);
            }
            yStack += h;
          });
          // company label
          ctx.fillStyle = '#333';
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(company, x0 + idx * spacing + spacing / 2, y0 + 20);
          // total label next to top of bar
          ctx.fillStyle = '#333';
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          const total = scoreRows[idx].total || contribs.reduce((a, b) => a + b, 0);
          const topY = y0 - (total / weightsTotal) * chartH - 6;
          ctx.fillText(String(Math.round(total)), x0 + idx * spacing + spacing / 2, topY);
        });
        // y axis
        ctx.strokeStyle = '#333';
        ctx.beginPath();
        ctx.moveTo(x0, margin.top);
        ctx.lineTo(x0, y0);
        ctx.stroke();

        // legend
        ctx.textAlign = 'left';
        ctx.font = '12px Arial';
        let lx = margin.left;
        let ly = 20;
        params.forEach((param, i) => {
          ctx.fillStyle = colors[i % colors.length];
          ctx.fillRect(lx, ly - 10, 14, 14);
          ctx.fillStyle = '#333';
          ctx.fillText(
            param.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
            lx + 20,
            ly + 2
          );
          lx += 180;
          if (lx > canvas.width - 150) {
            lx = margin.left;
            ly += 20;
          }
        });

        const dataUrl = canvas.toDataURL('image/png');
        const chartBytes = await dataURLToUint8Array(dataUrl);
        children.push(new Paragraph({ children: [new ImageRun({ data: chartBytes, transformation: { width: 640, height: 360 } })] }));
      }
    } catch (e) {
      // non-fatal
    }
  }

  if (Array.isArray(sbs?.['3_scoring_parameters'])) {
    const toTitle = (s) =>
      (s || '')
        .toString()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
    const rows = sbs['3_scoring_parameters'].map((p) => [toTitle(p.id) || 'N/A', p['1'] || 'N/A', p['3'] || 'N/A', p['5'] || 'N/A']);
    children.push(...createHeading('Scoring Parameters (1/3/5)', HeadingLevel.HEADING_2));
    children.push(createStyledTable(['Parameter', 'Score 1', 'Score 3', 'Score 5'], rows));
  }

  if (Array.isArray(sbs?.['7_multi_dimensional_comparison_matrices']?.feature_matrix)) {
    children.push(...createHeading('Feature Matrix', HeadingLevel.HEADING_2));

    const fm = sbs['7_multi_dimensional_comparison_matrices'].feature_matrix;
    // Collect union of companies across all features
    const companySet = new Set();
    fm.forEach((feat) => {
      Object.keys(feat?.scores || {}).forEach((c) => companySet.add(c));
    });
    const companiesList = Array.from(companySet);

    // Build header: Feature | ...companies | Notes
    const toTitle = (s) =>
      (s || '')
        .toString()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
    const headers = ['Feature', ...companiesList.map(toTitle), 'Notes'];

    // Build rows per feature
    const rows = fm.map((feat) => {
      const featureId = toTitle(feat?.id) || 'Feature';
      const notes = feat?._notes || '';
      const values = companiesList.map((c) => (feat?.scores && c in feat.scores ? feat.scores[c] : 'N/A'));
      return [featureId, ...values, notes];
    });

    // Compute column widths: Feature 20%, Notes 25%, remaining split among companies
    const featureCol = 0.2;
    const notesCol = 0.25;
    const remaining = Math.max(0, 1 - featureCol - notesCol);
    const perCompany = companiesList.length > 0 ? remaining / companiesList.length : remaining;
    const widths = [featureCol, ...companiesList.map(() => perCompany), notesCol];

    children.push(createStyledTable(headers, rows, widths));
  }

  if (Array.isArray(sbs?.['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard)) {
    children.push(...createHeading('Heatmap Scorecard', HeadingLevel.HEADING_2));
    const scoreRows = sbs['7_multi_dimensional_comparison_matrices'].A_heatmap_scorecard;

    // Derive parameter keys dynamically like UI: exclude 'company' and 'total'
    const paramKeys = scoreRows.length > 0 ? Object.keys(scoreRows[0]).filter((k) => k !== 'company' && k !== 'total') : [];

    const formatParam = (param) => param.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

    const headers = ['Company', ...paramKeys.map(formatParam), 'Total'];

    const rows = scoreRows.map((r) => [r.company || 'N/A', ...paramKeys.map((k) => String(r[k] ?? 'N/A')), String(r.total ?? 'N/A')]);

    // Column widths: Company 20%, Total 10%, params share remaining 70%
    const companyCol = 0.2;
    const totalCol = 0.1;
    const remaining = Math.max(0, 1 - companyCol - totalCol);
    const perParam = paramKeys.length > 0 ? remaining / paramKeys.length : remaining;
    const widths = [companyCol, ...paramKeys.map(() => perParam), totalCol];

    children.push(createStyledTable(headers, rows, widths));

    // Radar-like per company (simple polygon) – generated sequentially to ensure inclusion
    try {
      if (paramKeys.length && scoreRows.length) {
        // Section heading for the radar analysis overall
        children.push(...createHeading('Competitive Index - Radar Analysis', HeadingLevel.HEADING_2));
        for (const row of scoreRows) {
          const canvas = document.createElement('canvas');
          canvas.width = 960;
          canvas.height = 560;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          const cx = canvas.width / 2;
          const cy = canvas.height / 2 + 10;
          const radius = Math.min(canvas.width, canvas.height) * 0.38;
          const max = 5; // UI scale
          // axes + spokes + labels
          ctx.strokeStyle = '#e0e0e0';
          ctx.fillStyle = '#666';
          ctx.font = '11px Arial';
          ctx.textAlign = 'center';
          for (let i = 0; i < paramKeys.length; i++) {
            const angle = (Math.PI * 2 * i) / paramKeys.length - Math.PI / 2;
            const x = cx + radius * Math.cos(angle);
            const y = cy + radius * Math.sin(angle);
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(x, y);
            ctx.stroke();
            const lx = cx + (radius + 18) * Math.cos(angle);
            const ly = cy + (radius + 18) * Math.sin(angle);
            ctx.fillText(
              paramKeys[i].replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
              lx,
              ly
            );
          }
          for (let r = 1; r <= 5; r++) {
            ctx.beginPath();
            const rad = (r / 5) * radius;
            for (let i = 0; i < paramKeys.length; i++) {
              const angle = (Math.PI * 2 * i) / paramKeys.length - Math.PI / 2;
              const x = cx + rad * Math.cos(angle);
              const y = cy + rad * Math.sin(angle);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.stroke();
          }
          // polygon
          ctx.fillStyle = 'rgba(63,81,181,0.25)';
          ctx.strokeStyle = '#3f51b5';
          ctx.beginPath();
          paramKeys.forEach((k, i) => {
            const val = Math.max(0, Math.min(max, row[k] || 0));
            const rad = (val / max) * radius;
            const angle = (Math.PI * 2 * i) / paramKeys.length - Math.PI / 2;
            const x = cx + rad * Math.cos(angle);
            const y = cy + rad * Math.sin(angle);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          // title
          ctx.fillStyle = '#333';
          ctx.font = '14px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(row.company, cx, 26);

          const dataUrl = canvas.toDataURL('image/png');
          const img = await dataURLToUint8Array(dataUrl);
          children.push(new Paragraph({ children: [new ImageRun({ data: img, transformation: { width: 640, height: 400 } })] }));
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // Parameter Combination Matrices (soft threshold: render if >= 2 quadrants non-empty)
  try {
    const scoreRowsPCM = sbs?.['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];
    if (scoreRowsPCM.length) {
      const paramsPCM = Object.keys(scoreRowsPCM[0]).filter((k) => k !== 'company' && k !== 'total');
      // generate combinations
      for (let i = 0; i < paramsPCM.length; i++) {
        for (let j = i + 1; j < paramsPCM.length; j++) {
          const x = paramsPCM[i];
          const y = paramsPCM[j];
          const quads = { top_right: [], top_left: [], bottom_right: [], bottom_left: [] };
          scoreRowsPCM.forEach((r) => {
            const xv = Number(r[x] || 0);
            const yv = Number(r[y] || 0);
            const highX = xv >= 2.5;
            const highY = yv >= 2.5;
            const name = r.company || 'Company';
            if (highX && highY) quads.top_right.push(name);
            else if (highX && !highY) quads.bottom_right.push(name);
            else if (!highX && highY) quads.top_left.push(name);
            else quads.bottom_left.push(name);
          });
          const nonEmpty = ['top_right', 'top_left', 'bottom_right', 'bottom_left'].filter((q) => quads[q].length > 0).length;
          if (nonEmpty >= 2) {
            const xLabel = x.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
            const yLabel = y.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
            children.push(...createHeading(`${xLabel} vs ${yLabel}`, HeadingLevel.HEADING_2));
            const dataUrl = createParamPairMatrix(xLabel, yLabel, quads);
            const img = await dataURLToUint8Array(dataUrl);
            children.push(new Paragraph({ children: [new ImageRun({ data: img, transformation: { width: 640, height: 360 } })] }));
          }
        }
      }
    }
  } catch (e) {
    // ignore
  }

  if (sbs?.['7_multi_dimensional_comparison_matrices']?.trust_vs_user_base_2x2) {
    const quad = sbs['7_multi_dimensional_comparison_matrices'].trust_vs_user_base_2x2;
    children.push(...createHeading('Trust vs User Base (2x2)', HeadingLevel.HEADING_2));
    try {
      const dataUrl = createTrustUserBaseMatrix(quad);
      const img = await dataURLToUint8Array(dataUrl);
      children.push(new Paragraph({ children: [new ImageRun({ data: img, transformation: { width: 640, height: 360 } })] }));
    } catch (e) {
      const mkList = (title, arr) => [
        new Paragraph({ children: [new TextRun({ text: title, bold: true })] }),
        ...createBulletList(arr || [])
      ];
      children.push(...mkList('High Trust, Large Base', quad.high_trust_large_base));
      children.push(...mkList('High Trust, Small Base', quad.high_trust_small_base));
      children.push(...mkList('Low Trust, Large Base', quad.low_trust_large_base));
      children.push(...mkList('Low Trust, Small Base', quad.low_trust_small_base));
    }
  }

  // Strategic Analysis moved to Overview; skip here

  if (sbs?.['8_insight_distillation_and_validation']) {
    const idv = sbs['8_insight_distillation_and_validation'];
    if (Array.isArray(idv.parameter_by_parameter_analysis)) {
      children.push(...createHeading('Insight Distillation & Validation', HeadingLevel.HEADING_2));
      idv.parameter_by_parameter_analysis.forEach((row) => {
        // Format parameter key into Title Case
        const paramTitle = (row.parameter || 'Parameter')
          .toString()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (m) => m.toUpperCase());
        children.push(...createHeading(paramTitle, HeadingLevel.HEADING_3));

        // What / So What / Now What with bold keys and normal values
        if (row.what) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'What: ', bold: true }), new TextRun({ text: String(row.what) })],
              spacing: { before: 100, after: 100 }
            })
          );
        }
        if (row.so_what) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'So What: ', bold: true }), new TextRun({ text: String(row.so_what) })],
              spacing: { before: 100, after: 100 }
            })
          );
        }
        if (row.now_what) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: 'Now What: ', bold: true }), new TextRun({ text: String(row.now_what) })],
              spacing: { before: 100, after: 100 }
            })
          );
        }
      });
    }
  }

  // Executive recommendations already included in Overview; avoid duplicate rendering here

  if (sbs?.['1_data_normalization_and_gaps_CoT']) {
    children.push(...createHeading('Data Normalization & Gaps', HeadingLevel.HEADING_2));
    const dn = sbs['1_data_normalization_and_gaps_CoT'];
    if (Array.isArray(dn.CoT)) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'CoT Steps', bold: true })] }));
      children.push(...createBulletList(dn.CoT));
    }
    if (Array.isArray(dn.flagged_gaps)) {
      children.push(new Paragraph({ children: [new TextRun({ text: 'Flagged Gaps', bold: true })] }));
      children.push(...createBulletList(dn.flagged_gaps));
    }
  }

  // 7. Pricing Analysis (per-company)
  children.push(createSectionDivider());
  children.push(...createHeading('7. Pricing Analysis', HeadingLevel.HEADING_1, 'pricing-analysis'));
  if (Array.isArray(analysisResults?.analysis_results) && analysisResults.analysis_results.length) {
    analysisResults.analysis_results.forEach((company) => {
      const name = getServiceName(company.overview_result) || company.url || 'Company';
      const pricing = company?.pricing_analysis;
      if (!pricing || pricing.has_pricing_info === false) return;

      children.push(...createCompanyHeading(name));

      // Overview table: Pricing Model | Price Range | Billing Options | Free Plan | Trial
      const priceRange = pricing?.price_range || {};
      const freePlan = pricing?.free_plan || {};
      const trial = pricing?.trial_offering || {};
      const overviewRows = [
        ['Pricing Model', pricing?.pricing_model || 'N/A'],
        [
          'Price Range',
          priceRange.lowest && priceRange.highest ? `${priceRange.lowest} - ${priceRange.highest} ${priceRange.currency || ''}` : 'N/A'
        ],
        [
          'Billing Options',
          Array.isArray(pricing?.billing_options) && pricing.billing_options.length ? pricing.billing_options.join(', ') : 'N/A'
        ],
        ['Free Plan', freePlan.available ? freePlan.details || 'Available' : 'No'],
        [
          'Trial',
          trial.available ? (trial.duration ? `${trial.duration} (${trial.details || 'Available'})` : trial.details || 'Available') : 'No'
        ]
      ];
      children.push(createStyledTable(['Field', 'Value'], overviewRows, [0.25, 0.75]));

      // Pricing Strategy
      if (pricing?.pricing_strategy) {
        children.push(...createHeading('Pricing Strategy'));
        children.push(createText(pricing.pricing_strategy));
      }

      // Pricing Plans
      if (Array.isArray(pricing?.pricing_plans) && pricing.pricing_plans.length) {
        children.push(...createHeading('Pricing Plans'));
        const planRows = pricing.pricing_plans.map((plan) => [
          plan.plan_name || 'Plan',
          plan.price || 'N/A',
          plan.billing_frequency || 'N/A',
          Array.isArray(plan.key_features) && plan.key_features.length ? plan.key_features.join(', ') : 'N/A'
        ]);
        children.push(createStyledTable(['Plan', 'Price', 'Billing', 'Key Features'], planRows, [0.2, 0.2, 0.15, 0.45]));
      }

      // Features Comparison (plan-by-plan features)
      if (pricing?.features_comparison && typeof pricing.features_comparison === 'object') {
        const plans = Array.isArray(pricing?.pricing_plans) ? pricing.pricing_plans.map((p) => p.plan_name || 'Plan') : [];
        if (plans.length) {
          children.push(...createHeading('Features Comparison'));
          const headers = ['Feature', ...plans];
          const rows = Object.entries(pricing.features_comparison).map(([feature, values]) => [
            feature,
            ...values.map((v) => (v === true || v === 'Yes' ? 'Yes' : v === false || v === 'No' ? 'No' : String(v ?? 'N/A')))
          ]);
          children.push(createStyledTable(headers, rows));
        }
      }

      // Pricing Conclusion & Recommendations
      const conclusion = pricing?.pricing_conclusion || {};
      if (conclusion.summary) {
        children.push(...createHeading('Pricing Evaluation & Recommendations'));
        children.push(createText(conclusion.summary));
        if (Array.isArray(conclusion.recommendations) && conclusion.recommendations.length) {
          children.push(new Paragraph({ children: [new TextRun({ text: 'Recommendations', bold: true })] }));
          children.push(...createBulletList(conclusion.recommendations));
        }
      }

      // Pricing Weaknesses / Areas for Improvement
      if (Array.isArray(pricing?.pricing_weaknesses) && pricing.pricing_weaknesses.length) {
        children.push(...createHeading('Areas for Improvement'));
        children.push(...createBulletList(pricing.pricing_weaknesses));
      }

      // Plan comparison analysis
      if (
        pricing?.plan_comparison_analysis?.plans &&
        Array.isArray(pricing.plan_comparison_analysis.plans) &&
        pricing.plan_comparison_analysis.plans.length
      ) {
        children.push(...createHeading('Plans Analysis'));
        const rows = pricing.plan_comparison_analysis.plans.map((p) => [
          p.plan_name || 'Plan',
          p.use_cases || 'N/A',
          p.target_users || 'N/A',
          p.value_proposition || 'N/A'
        ]);
        children.push(createStyledTable(['Plan', 'Use Cases', 'Target Users', 'Value Proposition'], rows));
      }
    });
  }

  // Appendices at the very end
  // Appendix 1: Data Normalization & Gaps
  children.push(createSectionDivider());
  children.push(...createHeading('Appendix 1: Data Normalization & Gaps', HeadingLevel.HEADING_1, 'appendix-data-normalization'));
  {
    const sbsApp = analysisResults?.side_by_side_analysis?.analysis || {};
    const dn = sbsApp['1_data_normalization_and_gaps_CoT'];
    if (dn) {
      if (Array.isArray(dn.CoT) && dn.CoT.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'CoT Steps', bold: true })] }));
        children.push(...createBulletList(dn.CoT));
      }
      if (Array.isArray(dn.flagged_gaps) && dn.flagged_gaps.length) {
        children.push(new Paragraph({ children: [new TextRun({ text: 'Flagged Gaps', bold: true })] }));
        children.push(...createBulletList(dn.flagged_gaps));
      }
    }
  }

  // Appendix 2: Companies Overview
  children.push(createSectionDivider());
  children.push(...createHeading('Appendix 2: Companies Overview', HeadingLevel.HEADING_1, 'companies-overview'));
  if (companies.length > 0) {
    companies.forEach((company) => {
      const name = getServiceName(company.overview_result) || company.url || 'Company';
      children.push(...createCompanyHeading(name));
      const get = (key) => getFieldFromOverview(company.overview_result, key);
      if (get('Positioning')) {
        children.push(...createHeading('Positioning'));
        children.push(createText(get('Positioning')));
      }
      if (get('Tone of Voice')) {
        children.push(...createHeading('Tone of Voice'));
        children.push(createText(get('Tone of Voice')));
      }
      if (get('Pricing')) {
        children.push(...createHeading('Pricing'));
        children.push(createText(get('Pricing')));
      }
      if (get('CTA')) {
        children.push(...createHeading('Call to Action'));
        children.push(createText(get('CTA')));
      }
      children.push(...createHeading('Strengths'));
      children.push(createText(get('Strengths') || 'No strengths information available.'));
      children.push(...createHeading('Key Features'));
      children.push(createText(get('Key Features') || 'No key features available.'));
      children.push(...createHeading('Trust Factors'));
      children.push(createText(get('Trust Factors') || 'No trust factors available.'));
      children.push(...createHeading('Differentiation'));
      children.push(createText(get('Differentiation') || 'No differentiation information available.'));
      const usp = company?.usp;
      if (usp) {
        if (Array.isArray(usp.USP) && usp.USP.length) {
          children.push(...createHeading('Unique Selling Propositions'));
          children.push(...createBulletList(usp.USP));
        }
        if (Array.isArray(usp.DISTINCTION) && usp.DISTINCTION.length) {
          children.push(...createHeading('Distinctions'));
          children.push(...createBulletList(usp.DISTINCTION));
        }
      }
    });
  } else {
    children.push(createText('No companies available.'));
  }

  // Build document
  const doc = new Document({
    sections: [
      {
        children: titlePageContent,
        properties: { verticalAlign: VerticalAlign.CENTER }
      },
      {
        children,
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: logoBytes ? [new ImageRun({ data: logoBytes, transformation: { width: 146, height: 40 } })] : [],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 600 }
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'This analysis report was generated by Askpot AI. For more in-depth analysis visit ',
                    color: '999999',
                    size: 18
                  }),
                  new TextRun({ text: 'askpot.com', color: '999999', size: 18, bold: true })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 }
              })
            ]
          })
        }
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const defaultName = reportMeta?.title || reportMeta?.market || 'sbs-competitive-report';
  link.download = fileName || `${defaultName}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default exportSbsDocx;
